import React, { useEffect, useState } from "react";
import { Compass, X } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

/**
 * WelcomePollPrompt — gentle one-time nudge for newly-signed-up campers
 * to vote in the current poll. The first vote tells us their region
 * preference which we use immediately to route them to a matching
 * /browse?region= page — so they get to a useful screen on day-one
 * instead of bouncing.
 *
 * Triggers when ALL true:
 *   • User is authenticated
 *   • Account was created < 24h ago
 *   • Dismiss flag not yet set in localStorage
 *
 * Shows on first paint after a 1.2 s delay so it doesn't interrupt
 * the initial page settle. Self-dismisses on tap-outside, the X, or
 * the "Vote now" CTA (which smooth-scrolls to the embedded poll).
 */
const STORAGE_KEY = "cc_welcome_poll_prompt_shown_v1";

export default function WelcomePollPrompt() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    // Already dismissed in this browser?
    try {
      if (localStorage.getItem(STORAGE_KEY) === "1") return;
    } catch { /* storage blocked — show once per page load is fine */ }

    // Was the account created in the last 24h?
    const created = user.created_at ? Date.parse(user.created_at) : NaN;
    if (!Number.isFinite(created)) return;
    if (Date.now() - created > 24 * 60 * 60 * 1000) return;

    const t = setTimeout(() => setOpen(true), 1200);
    return () => clearTimeout(t);
  }, [user]);

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
    setOpen(false);
  };

  const onVoteCTA = () => {
    dismiss();
    // Smooth-scroll to the embedded WeekendRegionPoll on Landing.
    setTimeout(() => {
      const el = document.querySelector('[data-testid="weekend-poll"]')
        || document.querySelector('[data-testid="weekend-region-poll"]')
        || document.getElementById("weekend-region-poll");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 50);
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Welcome — vote in this week's poll"
      data-testid="welcome-poll-prompt"
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-4 bg-black/55"
      onClick={(e) => { if (e.target === e.currentTarget) dismiss(); }}
    >
      <div
        className="relative w-full max-w-md bg-white border-2 hairline-strong"
        style={{ animation: "cc-pop 220ms ease-out both" }}
      >
        {/* Sunny eyebrow */}
        <div
          className="px-5 pt-4 pb-3 text-white"
          style={{ background: "linear-gradient(135deg,#ff7a18 0%,#ff4500 100%)" }}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 mono text-[10px] uppercase tracking-[0.22em]">
              <Compass className="w-3.5 h-3.5" /> Welcome aboard
            </div>
            <button
              type="button"
              onClick={dismiss}
              className="text-white/85 hover:text-white"
              aria-label="Close"
              data-testid="welcome-poll-prompt-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="p-5 sm:p-6 space-y-4">
          <h2 className="font-display text-2xl leading-tight">
            One quick tap — and we'll know where to start you.
          </h2>
          <p className="text-sm text-[#333] leading-relaxed">
            <strong>Choosing your location is the first step to choosing your ideal campsite.</strong>{" "}
            Tap a region on this week's poll and we'll drop you straight onto a map of every
            campsite there — open today, dog-friendly, year-round, the lot.
          </p>

          <div className="flex flex-col-reverse sm:flex-row sm:items-center gap-2">
            <button
              type="button"
              onClick={dismiss}
              className="px-4 py-2.5 border hairline-strong text-[#111] text-sm font-bold mono uppercase tracking-[0.14em] hover:bg-[#fafafa] transition-colors"
              data-testid="welcome-poll-prompt-later"
            >
              Maybe later
            </button>
            <button
              type="button"
              onClick={onVoteCTA}
              className="flex-1 px-4 py-2.5 bg-[#ff4500] hover:bg-[#e03e00] text-white text-sm font-bold mono uppercase tracking-[0.14em] transition-colors"
              data-testid="welcome-poll-prompt-vote"
            >
              Vote now ⛺
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes cc-pop {
          0%   { opacity: 0; transform: translateY(12px) scale(0.97); }
          100% { opacity: 1; transform: translateY(0)    scale(1);    }
        }
      `}</style>
    </div>
  );
}
